import {Localisation} from "@domain/geolocalisation/localisation";
import {EMPTY, Observable,shareReplay} from "rxjs";
import {Adresse} from "@domain/profil/adresse";
import {GeolocalisationTool} from "@domain/geolocalisation/geolocalisationTool";
import {Result} from "@domain/common/http/result";
import {environment} from "@environments/environment";
import {first,map} from "rxjs/operators";
import {Injectable} from "@angular/core";
import {HttpClient} from "@angular/common/http";
import {LieuParam} from "@domain/common/lieu/lieu-param";
import {MatLegacyDialog as MatDialog} from "@angular/material/legacy-dialog";
import {LieuGeolocalisationListComponent} from "./selection/geolocalisation/lieu-geolocalisation-list.component";
import {ToastrService} from "ngx-toastr";
import {TranslateService} from "@ngx-translate/core";
import {TypeGeolocalisation} from "@domain/geolocalisation/typeGeolocalisation";
import {LieuService} from "./lieu.service";

/**
 * Service permettant d'ouvrir la popup de géolocalisation
 */
@Injectable()
export class LieuGeolocalisationService {

	/** Observable contenant l'outil de géolocalisation */
	private geolocalisationTool$: Observable<GeolocalisationTool> | null = null;

	/**
	 * Constructeur
	 */
	constructor(private http: HttpClient,
				private toastrService: ToastrService,
				private translateService: TranslateService,
				private matDialog: MatDialog,
                private lieuService: LieuService) {
	}

	/**
	 * Récupère l'outil de géolocalisation.<br>
	 * Si la géolocalisation est désactivée, l'outil est mis à TypeGeolocalisation.DISABLED.
	 * Le résultat est mis en cache dans le service après le premier appel.
	 */
	getGeolocalisationTool(): Observable<GeolocalisationTool> {
		if (!this.geolocalisationTool$) {
			this.geolocalisationTool$ = this.http.post<Result>(`${environment.baseUrl}/controller/Geolocalisation/getGeolocalisationTool`,null).pipe(
				first(),
				map(result => {
					//Si la géolocalisation n'est pas activée le back ne renvoie pas de geolocalisationTool : on la met à disabled
					const geolocalisationTool: GeolocalisationTool = result?.data ?? {};
					if (!geolocalisationTool.geolocalisationTool) {
						geolocalisationTool.geolocalisationTool = TypeGeolocalisation.DISABLED;
					}
					return geolocalisationTool;
				}),
				shareReplay(1)
			);
		}
		return this.geolocalisationTool$;
	}

	/**
	 * Ouvre la popup de géolocalisation
	 */
	openGeolocalisationTool(geolocalisationTool: GeolocalisationTool,
							localisation: Localisation,
							adresse: Adresse,
							lieuParam: LieuParam): Observable<GeolocalisationToolResult> | null {
		//Vérification de géolocalisation (activation, quota, toussa)
		if (this.isGeolocOk(geolocalisationTool)) {
			//Copie de l'objet
			const geolocalisation: Localisation = {
				...localisation ?? this.buildLocalisation(adresse)
			};

			//Pour Google Maps, on concatène les champs non vides
			const champsAdresse = [];

			//Ajout des champs non vides dans un tableau
			if (geolocalisation.rue) champsAdresse.push(geolocalisation.rue);
			if (geolocalisation.codePostal) champsAdresse.push(geolocalisation.codePostal);
			if (geolocalisation.ville) champsAdresse.push(geolocalisation.ville);
			if (geolocalisation.departement) champsAdresse.push(geolocalisation.departement);
			if (geolocalisation.pays) champsAdresse.push(geolocalisation.pays);

			//Pour Google Maps, on envoie toutes les infos dans le champ rue
			geolocalisation.rue = champsAdresse.join(" ");
			delete geolocalisation.adresse;

			//Affichage de la popup
			return this.matDialog.open<LieuGeolocalisationListComponent,any,GeolocalisationToolResult>(LieuGeolocalisationListComponent,{
				data: {
					adresse: adresse,
					geolocalisation: geolocalisation,
					lieuParam: lieuParam
				},
				minWidth: 600
			}).afterClosed();
		}

        return EMPTY;
	}

	/**
	 * Transforme une adresse en localisation
	 *
	 * @param adresse Adresse à transformer
	 */
	buildLocalisation(adresse: Adresse): Localisation {
		return {
			adresse: typeof adresse?.adresse === 'string' ? adresse?.adresse : this.lieuService.formatAdressetoString(adresse),
			rue: adresse?.rue,
			codePostal: adresse?.codePostal,
			ville: adresse?.ville,
			pays: adresse?.pays,
			codePays: adresse?.codePays
		} as Localisation;
	}

	/**
	 * Check si les paramètres de géolocalisation sont bien ok pour un appel
	 *
	 * @return true si les paramètres de géolocalisation sont ok, false sinon
	 */
	isGeolocOk(geolocalisationTool: GeolocalisationTool): boolean {
		//On vérifie que la géolocalisation est bien activée
		if (geolocalisationTool && geolocalisationTool.geolocalisationTool !== TypeGeolocalisation.DISABLED) {
			//Vérification du quota de requêtes
			if (geolocalisationTool.quotaLicence?.quotaAtteint) {
				//S'il est atteint, on toaste une erreur et on renvoie false
				this.toastrService.error(this.translateService.instant('lieu.quotaGeolocAtteint'));
				return false;
			}
			//Si pas de dépassement de quota, aucun problème on renvoie true
			return true;
		}
		//Sinon on toaste une erreur et on renvoie false
		this.toastrService.error(this.translateService.instant('lieu.geolocDesactivee'));
		return false;
	}
}

export type GeolocalisationToolResult = {
	geolocalisation: Localisation;
};

